import { useEffect, useState } from 'react';
import { FileText, Users, MessageSquare, FolderOpen } from 'lucide-react';
import { api } from '../lib/api';

interface Stats {
  totalDocuments: number;
  totalUsers: number;
  totalChats: number;
  totalCategories: number;
}

export function Dashboard() {
  const [stats, setStats] = useState<Stats>({
    totalDocuments: 0,
    totalUsers: 0,
    totalChats: 0,
    totalCategories: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch basic counts from existing endpoints
        const [docsRes, usersRes, catsRes] = await Promise.all([
          api.get('/api/documents'),
          api.get('/api/users'),
          api.get('/api/categories'),
        ]);

        setStats({
          totalDocuments: docsRes.data.data?.documents?.length || 0,
          totalUsers: usersRes.data.data?.users?.length || 0,
          totalChats: 0, // Would need an admin endpoint
          totalCategories: catsRes.data.data?.categories?.length || 0,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      label: 'Documentos',
      value: stats.totalDocuments,
      icon: FileText,
      color: 'emerald',
    },
    {
      label: 'Usuarios',
      value: stats.totalUsers,
      icon: Users,
      color: 'blue',
    },
    {
      label: 'Categorías',
      value: stats.totalCategories,
      icon: FolderOpen,
      color: 'purple',
    },
    {
      label: 'Conversaciones IA',
      value: stats.totalChats,
      icon: MessageSquare,
      color: 'orange',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400 mt-1">Resumen del sistema</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className="bg-slate-900 rounded-xl border border-slate-800 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {isLoading ? '-' : stat.value}
                  </p>
                </div>
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${stat.color}-500/10`}
                >
                  <Icon className={`w-6 h-6 text-${stat.color}-400`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <a
            href="/admin/documents"
            className="flex items-center gap-3 p-4 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <FileText className="w-5 h-5 text-emerald-400" />
            <span className="text-slate-200">Subir Documento</span>
          </a>
          <a
            href="/admin/users"
            className="flex items-center gap-3 p-4 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <Users className="w-5 h-5 text-inntea-400" />
            <span className="text-slate-200">Gestionar Usuarios</span>
          </a>
          <a
            href="/assistant"
            className="flex items-center gap-3 p-4 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <MessageSquare className="w-5 h-5 text-inntea-400" />
            <span className="text-slate-200">Abrir Asistente IA</span>
          </a>
        </div>
      </div>
    </div>
  );
}
